import { PointCloudObject } from "@/object-cache";
import {
  copyLotvMathVec3,
  EquirectangularDepthImage,
  getLotvMath,
  ImagePoint,
  InfiniteLine,
  lineLineIntersect,
  planeLineIntersect,
  planesIntersect,
  PointsSelectionResult,
  pointToLineDistanceSquared,
  selectPointsInSphere,
  TiledPano,
} from "@faro-lotv/lotv";
import { Line3, Plane, Sphere, Vector3 } from "three";

/**
 * Geometric features at a given position on a model
 */
export type GeometricFeatures = {
  /**  Normal at the position */
  normal: Vector3;

  /**
   * Edge found near the position. The edge is represented as an infinite line
   * with a point and a direction. The point is the projection of the given position
   * on the line.
   */
  edge?: InfiniteLine;

  /** Corner found near the position */
  corner?: Vector3;

  /** Detected planes at the position */
  planes?: Plane[];
};

/**
 * Compute geometric features of a point cloud at a given position
 *
 * @param pointcloud Point cloud object
 * @param position Position to compute the geometric features
 * @param searchRadius Search radius in meters used to collect points around the position for feature detection
 * @returns Detected geometric features
 */
export async function computePointCloudFeatures(
  pointcloud: PointCloudObject,
  position: Vector3,
  searchRadius: number,
): Promise<GeometricFeatures | undefined> {
  tmpObjs.sphere.set(position, searchRadius);
  const selection = selectPointsInSphere(pointcloud, {
    sphere: tmpObjs.sphere,
    maxNumberOfPoints: 1000,
  });
  if (!selection) return;

  return await computeGeometricFeatures(
    selection,
    position,
    searchRadius,
    false,
  );
}

/**
 *  Compute geometric features of a pano at a given position
 *
 * @param pano Pano object
 * @param depthImagePoint Depth image pixel point coordinate
 * @param maxPixelOffset Max pixel offset in the depth image, neighbour points are collected in a square
 * area around the center point [-maxPixelOffset, maxPixelOffset] in both directions.
 * @param maxSearchRadius Max 3d search radius in meters used to collect points around the position for feature detection
 * @returns Detected geometric features
 */
export async function computePanoFeatures(
  pano: TiledPano,
  depthImagePoint: ImagePoint,
  maxPixelOffset: number,
  maxSearchRadius: number,
): Promise<GeometricFeatures | undefined> {
  const { depths } = pano;
  if (!depths) return;

  const { row, col } = depthImagePoint;
  const center = depths.coord(row, col);
  if (!center) return;

  const { points, edgePoints } = tmpObjs;
  points.length = 0;
  edgePoints.length = 0;

  // And the max distance is limited to the selection radius
  const maxDistanceSq = maxSearchRadius * maxSearchRadius;
  for (let i = -maxPixelOffset; i <= maxPixelOffset; i++) {
    for (let j = -maxPixelOffset; j <= maxPixelOffset; j++) {
      const pt = depths.coord(row + i, col + j);
      if (!pt) continue;
      if (center.distanceToSquared(pt) < maxDistanceSq) {
        points.push(pt.x, pt.y, pt.z);
        // collect potential edge points
        const pointDepth = depths.depthAt(row + i, col + j);
        if (isEdgePointCandidate(depths, row + i, col + j, pointDepth)) {
          edgePoints.push(pt.x, pt.y, pt.z);
        }
      }
    }
  }

  const selection = {
    points: new Float32Array(points),
    origin: tmpObjs.origin,
  };

  const features = await computeGeometricFeatures(
    selection,
    center,
    maxSearchRadius,
    true,
  );

  if (!features) return;

  // If a lot of points were classified as edge points, it is likely that the points are noisy.
  // Skip pano edge detection when the edge points are more than 50% of the total points.
  if (edgePoints.length > 0 && edgePoints.length / points.length < 0.5) {
    await computePanoEdgeFeatures(center, edgePoints, maxDistanceSq, features);
  }

  return features;
}

// cached temporary objects for avoiding repleated allocation
const tmpObjs = {
  sphere: new Sphere(),
  normal: new Vector3(0, 0, 1),
  origin: new Vector3(0, 0, 0),
  planes: [new Plane(), new Plane(), new Plane()],
  planePoint: new Vector3(0, 0, 0),
  planeNormal: new Vector3(0, 0, 1),
  edgePoint: new Vector3(0, 0, 0),
  edgeTwoPlanes: {
    point: new Vector3(),
    direction: new Vector3(),
  },
  edgeLine1: {
    point: new Vector3(),
    direction: new Vector3(),
  },
  edgeLine2: {
    point: new Vector3(),
    direction: new Vector3(),
  },
  cornerPoint: new Vector3(0, 0, 0),
  points: new Array<number>(),
  edgePoints: new Array<number>(),
  direction1: new Vector3(0, 0, 0),
  direction2: new Vector3(0, 0, 0),
  lineIntersect: new Line3(),
};

// The depth discontinuity threshold used to detect edge points in the depth image.
const DEPTH_DISCONTINUITY_THRESHOLD = 0.1;

/**
 * Check if the pixel could be a potential edge point. A depth image pixel is considered to be an
 * edge point candidate if it has a depth discontinuity in its immediate neighbourhood pixels.
 *
 * @param depths Reference to the depth image
 * @param row Point's row in the depth image
 * @param col Point's column in the depth image
 * @param pointDepth Point's depth in the depth image
 * @returns True if the point is a potential edge point, false otherwise
 */
function isEdgePointCandidate(
  depths: EquirectangularDepthImage,
  row: number,
  col: number,
  pointDepth: number,
): boolean {
  for (let i = -1; i <= 1; i++) {
    for (let j = -1; j <= 1; j++) {
      if (i === 0 && j === 0) continue;
      const d = depths.depthAt(row + i, col + j);
      if (Math.abs(d - pointDepth) > DEPTH_DISCONTINUITY_THRESHOLD) {
        return true;
      }
    }
  }
  return false;
}

// Try to find edge or corner from the edge points using RANSAC line fitting algorithm.
async function computePanoEdgeFeatures(
  center: Vector3,
  edgePoints: number[],
  maxDistanceSq: number,
  features: GeometricFeatures,
): Promise<void> {
  if (features.edge && features.corner) {
    // edge and corner already found, no need to compute again
    return;
  }

  const pointsToFit = new Float32Array(edgePoints);
  const LINE_RANSAC_THRESHOLD = 0.005;
  const lotvMath = await getLotvMath();
  const fitLines = lotvMath.findLines(
    pointsToFit,
    LINE_RANSAC_THRESHOLD,
    true,
    2,
    0.1,
    false,
  );
  if (!fitLines || fitLines.length === 0) return;

  const edge1 = tmpObjs.edgeLine1;
  copyLotvMathVec3(fitLines[0].point, edge1.point);
  copyLotvMathVec3(fitLines[0].direction, edge1.direction);

  if (!features.edge) {
    // no edge found yet, use the first line as the edge
    features.edge = createEdgeFromLine(center, edge1, maxDistanceSq);

    // check for corner if there is a second line
    if (fitLines.length === 2) {
      const edge2 = tmpObjs.edgeLine2;
      copyLotvMathVec3(fitLines[1].point, edge2.point);
      copyLotvMathVec3(fitLines[1].direction, edge2.direction);
      // the 2nd line should not be too close to the first line
      if (isAngleValid(edge1.direction, edge2.direction)) {
        const intersect = lineLineIntersect(
          edge1,
          edge2,
          tmpObjs.lineIntersect,
        );
        if (intersect) {
          features.corner = intersect.getCenter(tmpObjs.cornerPoint);
        }
      }
    }
  } else if (
    !features.corner &&
    features.planes &&
    features.planes.length > 0
  ) {
    // edge already found from plane extraction, try compute corner from the
    // first line and previous extracted plane with normal closest to the line
    const line1 = createEdgeFromLine(center, edge1, maxDistanceSq);
    if (line1) {
      let maxDotVal = -Infinity;
      let planeToUse = features.planes[0];
      for (const plane of features.planes) {
        const dotVal = Math.abs(plane.normal.dot(line1.direction));
        if (dotVal > maxDotVal) {
          maxDotVal = dotVal;
          planeToUse = plane;
        }
      }
      const corner = planeLineIntersect(planeToUse, line1, tmpObjs.cornerPoint);
      if (corner && corner.distanceToSquared(center) < maxDistanceSq) {
        features.edge = line1;
        features.corner = corner;
      }
    }
  }
}

// Due to the noise in the scans, the algorithm often finds 2 or more planes very close to
// each other, with small angle between them. We need to filter out these type of planes
// below an angle threshold. The value 0.436332 radians(25 degrees) is chosen as a
// reasonable threshold to filter out the planes that are too close to each other.
const ANGLE_THRESHOLD = 0.436332;
const ANGLE_THRESHOLD_COS = Math.cos(ANGLE_THRESHOLD);
// Check angle between two directions should be greater than the above threshold.
function isAngleValid(dir1: Vector3, dir2: Vector3): boolean {
  return Math.abs(dir1.dot(dir2)) < ANGLE_THRESHOLD_COS;
}

async function computeGeometricFeatures(
  selection: PointsSelectionResult,
  position: Vector3,
  maxSearchRadius: number,
  isPanoPoints: boolean,
): Promise<GeometricFeatures | undefined> {
  const lotvMath = await getLotvMath();
  const fitResult = lotvMath.fitPlane(selection.points);
  if (!fitResult) return;

  const normal = copyLotvMathVec3(fitResult.normal, tmpObjs.normal);

  const CURVATURE_THRESHOLD = 0.001;
  if (
    fitResult.curvature &&
    Math.abs(fitResult.curvature) < CURVATURE_THRESHOLD
  ) {
    // the curvature is too small, local shape is almost flat, no edge/corner
    return { normal };
  }

  // try to extract max 3 planes from points
  const PLANE_RANSAC_THRESHOLD = 0.005;
  const fitPlanes = lotvMath.findPlanes(
    selection.points,
    PLANE_RANSAC_THRESHOLD,
    true,
    3,
    0.1,
    true,
  );
  if (!fitPlanes || fitPlanes.length === 0) {
    return { normal };
  }

  // only one plane found, no edge/corner, update normal to the ransac plane normal
  if (fitPlanes.length === 1) {
    copyLotvMathVec3(fitPlanes[0].normal, normal);
    return { normal };
  }

  const planes = [];
  for (let i = 0; i < fitPlanes.length; i++) {
    copyLotvMathVec3(fitPlanes[i].normal, tmpObjs.planeNormal);
    copyLotvMathVec3(fitPlanes[i].point, tmpObjs.planePoint);
    planes.push(
      tmpObjs.planes[i].setFromNormalAndCoplanarPoint(
        tmpObjs.planeNormal,
        tmpObjs.planePoint.add(selection.origin),
      ),
    );
  }

  updateNormalToClosestPlane(planes, position, normal);

  if (!isAngleValid(planes[0].normal, planes[1].normal)) {
    return { normal, planes };
  }

  const intersect = planesIntersect(
    planes[0],
    planes[1],
    tmpObjs.edgeTwoPlanes,
  );
  if (!intersect) {
    return { normal, planes };
  }

  const maxDistanceSq = maxSearchRadius * maxSearchRadius;
  const edge = createEdgeFromLine(position, intersect, maxSearchRadius);
  if (!edge) {
    // no edge found
    return { normal, edge, planes };
  }

  if (
    isPanoPoints &&
    fitPlanes[0].inlierIndices &&
    fitPlanes[1].inlierIndices
  ) {
    // Points computed from pano pixel coordinates and depth normally have zigzag edges and other type of noise.
    // The RANSAC threshold used to extract two planes is too strict to check if the intersect line contains
    // points from both planes. A bigger threshold, 2x the RANSAC threshold, is used here.
    const maxLineDistanceSq =
      PLANE_RANSAC_THRESHOLD * PLANE_RANSAC_THRESHOLD * 4;
    const isLineContainsPoints0 = doesLineContainPoints(
      edge,
      selection.points,
      fitPlanes[0].inlierIndices,
      maxLineDistanceSq,
    );
    const isLineContainsPoints1 = doesLineContainPoints(
      edge,
      selection.points,
      fitPlanes[1].inlierIndices,
      maxLineDistanceSq,
    );
    if (!(isLineContainsPoints0 && isLineContainsPoints1)) {
      // the edge does not contain points from both planes, so it is not a valid edge
      return { normal, planes };
    }
  }

  // check for corner if there is a third plane
  let corner = undefined;
  if (planes.length === 3) {
    // the 3rd plane should not be too close to the first two planes
    if (
      isAngleValid(planes[2].normal, planes[0].normal) &&
      isAngleValid(planes[2].normal, planes[1].normal)
    ) {
      corner = planeLineIntersect(planes[2], edge, tmpObjs.cornerPoint);
      if (corner) {
        // discard if the corner is too far from the position
        if (corner.distanceToSquared(position) > maxDistanceSq) {
          corner = undefined;
        }
      }
    }
  }

  return { normal, edge, corner, planes };
}

// Create an edge by projecting the position to an edge line and checking
// the distance to the line. If the distance is too large, the edge is ignored.
function createEdgeFromLine(
  position: Vector3,
  line: InfiniteLine,
  maxDistanceSq: number,
): InfiniteLine | undefined {
  // project the position to the intersection line
  const edgePoint = tmpObjs.edgePoint.subVectors(position, line.point);
  const offset = edgePoint.dot(line.direction);
  edgePoint.copy(line.point);
  edgePoint.addScaledVector(line.direction, offset);
  if (edgePoint.distanceToSquared(position) > maxDistanceSq) {
    // the edge is too far from the position, ignore it
    return;
  }
  // the edge is close enough to the position, use it
  return {
    point: edgePoint,
    direction: line.direction,
  };
}

// The best fit normal is the average of all neighbour points. Most time it
// is not the correct normal when there are multiple planes detected.
// Adjust the normal to be the closest plane to the position
function updateNormalToClosestPlane(
  planes: Plane[],
  position: Vector3,
  normal: Vector3,
): void {
  let minDistance = Infinity;
  for (const plane of planes) {
    const distance = Math.abs(plane.distanceToPoint(position));
    if (distance < minDistance) {
      minDistance = distance;
      normal.copy(plane.normal);
    }
  }
}

// Check if the line contains any of the points in the indices
function doesLineContainPoints(
  line: InfiniteLine,
  points: Float32Array,
  indices: number[],
  maxDistanceSq: number,
): boolean {
  const tmpPoint = new Vector3();
  for (const index of indices) {
    tmpPoint.fromArray(points, index * 3);
    if (pointToLineDistanceSquared(tmpPoint, line) < maxDistanceSq) {
      return true;
    }
  }
  return false;
}
